import { BaseListViewStore, ShippingCostCellData, DateTimeCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { ShipmentInfoReturnStore } from 'modules/shipment-info-return/stores';
import { BaasicTableSettingsViewStore } from 'common/stores';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	isSuperAdminOrganization,
} from 'core/utils';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName } from 'common/constants';
import { CarrierServiceService, ApplicationService, OrderService, CarrierService } from 'common/data';
import { HyperLinkTrackingNumberCellData } from 'core/stores/CellDataStore';
import _ from 'lodash';

class ShipmentInfoReturnListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable showReturnShipments = false;
	isSuperAdmin = false;
	isReturn = false;
	showActions = false;
	localStorageWidthKey = 'shipment-info-returns-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ShipmentInfoReturnStore(rootStore),
			queryParams: {
				embed: [
					'shipmentInfoStatus',
					'order',
					'carrierService',
					'carrier',
					'carrierDeliveryStatus',
					'carrierDeliveryStatus.shipmentInfoDeliveryStatus',
					'seller',
				],
				filter: localStorageHandler.get('shipment-info-returns-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'trackingNumber',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGNUMBER',
						dataCell: HyperLinkTrackingNumberCellData,
						carrierServicePath: 'carrierService.name',
						shippedOnly: true,
						selected: true,
						width: 200,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						propertyToCheckForCarrierServiceName: 'carrierService.name',
						propertyToCheckForActiveTrackingNumber: 'shipmentInfoStatus.abrv',
					},
					{
						key: 'seller.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SELLER',
						sellerOnly: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						locked: false,
						sortable: true,
						dontSplit: true,
						service: 'applicationService',
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'order.orderNumber',
						title: 'SHIPMENTSTATUSVIEW.ORDERNUMBER',
						service: 'orderService',
						checkPartiallyFulfillMixedOrders: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'deliveryDate',
						title: 'SHIPMENTSTATUSVIEW.ESTIMATEDDELIVERYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'MM/DD/YYYY HH:mm',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'cost',
						title: 'SHIPMENTSTATUSVIEW.COST',
						selected: false,
						dataCell: ShippingCostCellData,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrierService.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERSERVICE',
						service: 'carrierServiceService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrier.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIER',
						service: 'carrierService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrierDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERDELIVERYSTATUS',
						lookupConstant: lookupRouteName.carrierDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus'],
					},
					{
						key: 'carrierDeliveryStatus.shipmentInfoDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.SHIPMENTINFODELIVERYSTATUS',
						lookupConstant: lookupRouteName.shipmentInfoDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus', 'carrierDeliveryStatus.shipmentInfoDeliveryStatus'],
					},
					{
						key: 'internalNote',
						title: 'SHIPMENTSTATUSVIEW.INTERNALNOTE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'zone',
						title: 'SHIPMENTSTATUSVIEW.ZONE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
					},
				],
				sortObject: [
					{
						field: 'trackingNumber',
						dir: 'asc',
					},
				],
				actions: {},
			},
			permissions: {
				update: () => this.authStore.hasPermission('fulfillmentSection.update'),
				create: () => this.authStore.hasPermission('fulfillmentSection.create'),
				remove: () => this.authStore.hasPermission('fulfillmentSection.remove'),
				read: () => this.authStore.hasPermission('fulfillmentSection.read'),
				full: () => this.authStore.hasPermission('fulfillmentSection.full'),
			},
			routes: {},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findShipmentInfoReturns);
		checkForColumnFilter(localStorageHandler.get('shipment-info-returns-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		(this.returnStore = new ShipmentInfoReturnStore(rootStore)),
			(this.tableFilter = new TableFilterUtility({
				lookupStore: new LookupStore(rootStore),
				listStore: this.listStore,
				queryUtility: this.queryUtility,
				columns: this.tableData.columns,
				services: [
					this.listStore.service,
					new OrderService(rootStore.app.baasic.apiClient),
					new CarrierServiceService(rootStore.app.baasic.apiClient),
					new ApplicationService(rootStore.app.baasic.apiClient),
					new CarrierService(rootStore.app.baasic.apiClient),
				],
			}));
		this.setSearchVisible();
		this.setButtons();
	}

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		});
	}

	@action.bound
	findShipmentInfoReturns = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ShipmentInfoReturnTableSettings',
			this.setAsChanged,
			this.findShipmentInfoReturns
		);
		this.queryUtility.setFilterParams(this, { ignoreEmptyTracking: true, shippedOnly: true }, this.tableFilter);
		await this.listStore.findShipmentInfoReturn(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'shipment-info-returns-filter', 'shipment-info-returns-table-data');
		this.setButtons();
		this.resume();
	};

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}
}

export default ShipmentInfoReturnListViewStore;



// WEBPACK FOOTER //
// ./src/modules/shipment-info-return/stores/ShipmentInfoReturnListViewStore.js